import { CmtsApiError as LegacyCmtsApiError, type CmtsErrorEnvelope } from "../api/cmtsApiErrors.js";
import { catalogEntryForErrorCode, isKnownCmtsBackendErrorCode } from "./cmtsApiErrorCatalog.js";
import { cmtsDomainRemediation } from "./cmtsApiErrorRemediation.js";
import type { CmtsApiError, CmtsApiErrorCategory, CmtsApiErrorContext, CmtsApiErrorSeverity, CmtsDependencyError } from "./cmtsApiErrorTypes.js";

const canonicalAliasByLegacyCode: Record<string, string> = {
  CMTS_ERR_INVALID_PAYLOAD: "invalid_payload",
  CMTS_ERR_FAILED_VALIDATION: "validation_failed",
  CMTS_ERR_MISSING_ENTITY: "missing_entity",
  CMTS_ERR_DUPLICATE_BUSINESS_KEY: "duplicate_business_key",
  CMTS_ERR_MISSING_DEPENDENCY: "dependency_exists",
  CMTS_ERR_BROKEN_REFERENCE: "missing_reference_value",
  CMTS_ERR_INACTIVE_MASTER_REFERENCE: "inactive_reference_used",
  CMTS_ERR_CROSS_TENANT_MISMATCH: "cross_tenant_mismatch",
  CMTS_ERR_CROSS_ORG_MISMATCH: "cross_org_mismatch",
  CMTS_ERR_DEACTIVATE_BLOCKED: "deactivation_blocked",
  CMTS_ERR_FAILED_GUARD_EVALUATION: "invalid_workflow_transition",
  CMTS_ERR_UNSUPPORTED_ACTION_CODE: "unsupported_workflow_action",
  CMTS_ERR_ACCESS_DENIED: "rbac_blocked",
  CMTS_ERR_SOD_BLOCKED: "sod_blocked",
  CMTS_ERR_STALE_SNAPSHOT: "stale_snapshot",
  CMTS_ERR_STALE_CLIENT_SNAPSHOT: "stale_snapshot",
  CMTS_ERR_API_CONFLICT: "concurrency_conflict",
  CMTS_ERR_SIDE_EFFECT_IDEMPOTENCY_CONFLICT: "idempotency_conflict",
  CMTS_ERR_API_UNAVAILABLE: "persistence_failure",
};

export function normalizeCmtsApiError(error: unknown, context?: CmtsApiErrorContext | undefined): CmtsApiError {
  if (isAbortLike(error)) {
    return buildError({
      code: "request_aborted",
      message: "The request was cancelled.",
      category: "network",
      severity: "info",
      retryable: false,
      httpStatus: 0,
      fieldErrors: {},
      dependencyErrors: [],
      context,
      remediation: "No action is required when cancellation was caused by navigation, debounce, or manual abort.",
    });
  }

  const response = extractResponse(error);
  const status = response.status;
  const body = response.body;
  const candidateCode = normalizeBackendCode(
    firstString(
      readRecordString(body, "code"),
      readRecordString(body, "errorCode"),
      readRecordString(body, "error_code"),
      error instanceof LegacyCmtsApiError ? error.envelope.code : undefined,
      statusCodeToDefaultCode(status),
    ),
  );
  const catalog = catalogEntryForErrorCode(candidateCode);
  const fieldErrors = parseFieldErrors(body, error);
  const dependencyErrors = parseDependencyErrors(body, error);
  const code = catalog?.code ?? candidateCode;
  const category = catalog?.category ?? categoryFromStatus(status, Boolean(Object.keys(fieldErrors).length), Boolean(dependencyErrors.length));
  const severity = catalog?.severity ?? severityFromStatus(status, category);
  const retryable = catalog?.retryable ?? retryableFromStatus(status, category, code);
  const fallbackMessage = messageFromStatus(status, category);
  const message = cleanMessage(
    firstString(
      readRecordString(body, "message"),
      readRecordString(body, "detail"),
      readRecordString(body, "title"),
      error instanceof Error ? error.message : undefined,
      catalog?.defaultMessage,
      fallbackMessage,
    ),
    fallbackMessage,
  );
  const remediation = cmtsDomainRemediation(code, catalog?.remediation ?? remediationFromStatus(status, category), context);
  const correlationId = firstString(
    readRecordString(body, "correlationId"),
    readRecordString(body, "traceId"),
    readHeaderValue(response.headers, "x-correlation-id"),
    readHeaderValue(response.headers, "x-request-id"),
    readHeaderValue(response.headers, "traceparent"),
    error instanceof LegacyCmtsApiError ? error.envelope.correlationId : undefined,
  );
  return buildError({
    code,
    message,
    category,
    severity,
    retryable,
    httpStatus: status,
    fieldErrors,
    dependencyErrors,
    correlationId,
    context,
    remediation,
    technicalDetails: technicalDetailsFor(status, code, response.method, response.url),
  });
}

export function fieldErrorsForAliases(errors: Record<string, string[]>, fieldName: string, aliases: readonly string[] = []): string[] {
  const keys = [fieldName, ...aliases];
  const found: string[] = [];
  for (const key of keys) {
    const values = errors[key];
    if (Array.isArray(values)) found.push(...values.filter((item) => item.trim().length > 0));
  }
  return Array.from(new Set(found));
}

type ExtractedResponse = {
  readonly status?: number | undefined;
  readonly body: unknown;
  readonly headers: unknown;
  readonly method?: string | undefined;
  readonly url?: string | undefined;
};

function buildError(input: {
  readonly code: string;
  readonly message: string;
  readonly category: CmtsApiErrorCategory;
  readonly severity: CmtsApiErrorSeverity;
  readonly retryable: boolean;
  readonly httpStatus?: number | undefined;
  readonly fieldErrors: Record<string, string[]>;
  readonly dependencyErrors: CmtsDependencyError[];
  readonly correlationId?: string | undefined;
  readonly context?: CmtsApiErrorContext | undefined;
  readonly remediation: string;
  readonly technicalDetails?: string | undefined;
}): CmtsApiError {
  const safeDiagnostics: Record<string, string | number | boolean | null> = {
    code: input.code,
    category: input.category,
    severity: input.severity,
    retryable: input.retryable,
  };
  if (input.httpStatus !== undefined) safeDiagnostics.httpStatus = input.httpStatus;
  if (input.context?.operation) safeDiagnostics.operation = input.context.operation;
  if (input.context?.route) safeDiagnostics.route = input.context.route;
  if (input.correlationId) safeDiagnostics.correlationId = input.correlationId;
  return {
    code: input.code,
    message: input.message,
    category: input.category,
    severity: input.severity,
    ...(input.httpStatus !== undefined ? { httpStatus: input.httpStatus } : {}),
    fieldErrors: input.fieldErrors,
    dependencyErrors: input.dependencyErrors,
    ...(input.context?.permission ? { permission: input.context.permission } : {}),
    ...(input.context?.actionCode ? { actionCode: input.context.actionCode } : {}),
    ...(input.context?.entityType ? { entityType: input.context.entityType } : {}),
    ...(input.context?.entityId ? { entityId: input.context.entityId } : {}),
    ...(input.correlationId ? { correlationId: input.correlationId } : {}),
    retryable: input.retryable,
    remediation: input.remediation,
    ...(input.technicalDetails ? { technicalDetails: input.technicalDetails } : {}),
    safeDiagnostics,
  };
}

function extractResponse(error: unknown): ExtractedResponse {
  if (error instanceof LegacyCmtsApiError) {
    return { status: error.envelope.status, body: envelopeToBody(error.envelope), headers: {}, method: undefined, url: undefined };
  }
  if (error instanceof Error) {
    const timeout = /timeout|timed out|ECONNABORTED/i.test(error.message);
    return { status: timeout ? 0 : undefined, body: { code: timeout ? "network_timeout" : "unexpected_error", message: error.message }, headers: {}, method: undefined, url: undefined };
  }
  if (isRecord(error)) {
    const response = isRecord(error.response) ? error.response : undefined;
    const config = isRecord(error.config) ? error.config : undefined;
    if (response) {
      return {
        status: readNumber(response.status),
        body: response.data,
        headers: response.headers,
        method: readString(config?.method)?.toUpperCase(),
        url: readString(config?.url),
      };
    }
    return {
      status: readNumber(error.status) ?? statusFromCode(readString(error.code)),
      body: error,
      headers: error.headers,
      method: readString(error.method)?.toUpperCase(),
      url: readString(error.url),
    };
  }
  return { status: undefined, body: { message: "An unexpected CMTS error occurred." }, headers: {}, method: undefined, url: undefined };
}

function envelopeToBody(envelope: CmtsErrorEnvelope): Record<string, unknown> {
  return {
    code: envelope.code,
    message: envelope.message,
    correlationId: envelope.correlationId,
    errors: envelope.errors,
    details: envelope.details,
    dependencies: readUnknownArray(envelope.details, "dependencies"),
    blockingDependencies: readUnknownArray(envelope.details, "blockingDependencies"),
  };
}

function normalizeBackendCode(value: string): string {
  const trimmed = value.trim();
  if (isKnownCmtsBackendErrorCode(trimmed)) return trimmed;
  const alias = canonicalAliasByLegacyCode[trimmed];
  if (alias) return alias;
  const lower = trimmed.toLowerCase();
  if (isKnownCmtsBackendErrorCode(lower)) return lower;
  if (lower.includes("timeout")) return "network_timeout";
  if (lower.includes("cancel")) return "request_aborted";
  return lower || "unexpected_error";
}

function statusCodeToDefaultCode(status: number | undefined): string {
  if (status === 400) return "invalid_payload";
  if (status === 401) return "auth_required";
  if (status === 403) return "rbac_blocked";
  if (status === 404) return "missing_entity";
  if (status === 409) return "concurrency_conflict";
  if (status === 422) return "validation_failed";
  if (status && status >= 500) return "persistence_failure";
  if (status === 0) return "network_timeout";
  return "unexpected_error";
}

function statusFromCode(code: string | undefined): number | undefined {
  if (!code) return undefined;
  if (/timeout|network/i.test(code)) return 0;
  return undefined;
}

function parseFieldErrors(body: unknown, error: unknown): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  const candidates: unknown[] = [];
  if (isRecord(body)) {
    candidates.push(body.fieldErrors, body.errors);
    if (Array.isArray(body.details)) candidates.push(body.details);
    if (isRecord(body.details)) candidates.push(body.details.fieldErrors, body.details.errors);
  }
  if (error instanceof LegacyCmtsApiError) candidates.push(error.envelope.errors);
  for (const candidate of candidates) {
    mergeFieldErrors(result, candidate);
  }
  return result;
}

function mergeFieldErrors(target: Record<string, string[]>, candidate: unknown): void {
  if (!candidate) return;
  if (isRecord(candidate)) {
    for (const [key, value] of Object.entries(candidate)) {
      const messages = Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : typeof value === "string" ? [value] : [];
      if (messages.length > 0) target[key] = unique([...(target[key] ?? []), ...messages]);
    }
    return;
  }
  if (Array.isArray(candidate)) {
    for (const item of candidate) {
      if (!isRecord(item)) continue;
      const field = readString(item.field) ?? readString(item.propertyName) ?? readString(item.name);
      const message = readString(item.message) ?? readString(item.errorMessage) ?? readString(item.reason);
      if (field && message) target[field] = unique([...(target[field] ?? []), message]);
    }
  }
}

function parseDependencyErrors(body: unknown, error: unknown): CmtsDependencyError[] {
  const items: CmtsDependencyError[] = [];
  const candidates: unknown[] = [];
  if (isRecord(body)) {
    candidates.push(body.dependencies, body.blockingDependencies, body.dependencyErrors);
    if (isRecord(body.details)) candidates.push(body.details.dependencies, body.details.blockingDependencies, body.details.dependencyErrors);
  }
  if (error instanceof LegacyCmtsApiError && isRecord(error.envelope.details)) {
    candidates.push(error.envelope.details.dependencies, error.envelope.details.blockingDependencies, error.envelope.details.dependencyErrors);
  }
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    for (const item of candidate) {
      if (!isRecord(item)) continue;
      const entityType = readString(item.entityType) ?? readString(item.entity) ?? readString(item.type) ?? "Dependency";
      const entityId = readString(item.entityId) ?? readString(item.id);
      const displayName = readString(item.displayName) ?? readString(item.name) ?? readString(item.reference);
      const reason = readString(item.reason) ?? readString(item.message) ?? "Dependent record blocks this action.";
      const count = readNumber(item.count);
      items.push({ entityType, ...(entityId ? { entityId } : {}), ...(displayName ? { displayName } : {}), reason, ...(count !== undefined ? { count } : {}), blocking: true });
    }
  }
  return items;
}

function categoryFromStatus(status: number | undefined, hasFieldErrors: boolean, hasDependencyErrors: boolean): CmtsApiErrorCategory {
  if (hasFieldErrors) return "field_validation";
  if (hasDependencyErrors) return "dependency";
  if (status === 401 || status === 403) return "authorization";
  if (status === 404) return "route";
  if (status === 409) return "concurrency";
  if (status === 400 || status === 422) return "validation";
  if (status === 0) return "network";
  if (status && status >= 500) return "persistence";
  return "unexpected";
}

function severityFromStatus(status: number | undefined, category: CmtsApiErrorCategory): CmtsApiErrorSeverity {
  if (category === "authorization" || category === "dependency" || category === "workflow" || category === "segregation_of_duties") return "blocking";
  if (category === "concurrency" || category === "route") return "warning";
  if (status && status >= 500) return "error";
  return "error";
}

function retryableFromStatus(status: number | undefined, category: CmtsApiErrorCategory, code: string): boolean {
  if (code === "request_aborted") return false;
  if (code === "invalid_workflow_transition" || code === "stale_snapshot" || code === "concurrency_conflict" || code === "evidence_missing") return true;
  if (category === "network" || category === "persistence") return true;
  return Boolean(status && status >= 500);
}

function messageFromStatus(status: number | undefined, category: CmtsApiErrorCategory): string {
  if (status === 401) return "Authentication expired or was rejected.";
  if (status === 403) return "Permission denied for this CMTS action.";
  if (status === 404) return "The requested CMTS record was not found.";
  if (status === 409) return "The request conflicts with the latest backend state.";
  if (status === 0 || category === "network") return "The CMTS API request timed out or the network is unavailable.";
  if (status && status >= 500) return "The CMTS backend could not complete the request.";
  return "The CMTS request could not be completed.";
}

function remediationFromStatus(status: number | undefined, category: CmtsApiErrorCategory): string {
  if (status === 401) return "Sign in again after session refresh fails.";
  if (status === 403) return "Request the required permission or switch to an authorized user.";
  if (status === 404) return "Refresh the list or return to the previous page.";
  if (status === 409 || category === "concurrency") return "Refresh the record and retry with the latest backend state.";
  if (category === "network") return "Check connectivity and retry. Do not assume the operation succeeded.";
  if (category === "persistence") return "Retry later or contact support with the correlation ID.";
  return "Review CMTS diagnostics and correct the request before retrying.";
}

function technicalDetailsFor(status: number | undefined, code: string, method?: string, url?: string): string | undefined {
  const parts = [`code=${code}`];
  if (status !== undefined) parts.push(`status=${String(status)}`);
  if (method) parts.push(`method=${method}`);
  if (url) parts.push(`route=${sanitizeRoute(url)}`);
  return parts.join("; ");
}

function sanitizeRoute(value: string): string {
  return value.replace(/[?&](access_token|refresh_token|token|authorization)=[^&]+/gi, "$1=[redacted]");
}

function cleanMessage(message: string, fallback: string): string {
  if (!message.trim()) return fallback;
  const technicalPattern = /\bat\s+\S+\.(?:tsx?|jsx?|cs):\d+(?::\d+)?|stack trace|node_modules|webpack|vite|System\.[A-Za-z]+Exception|Microsoft\.|Exception:|Npgsql|SqlException|PostgresException|EntityFrameworkCore|\bSELECT\s+|\bINSERT\s+|\bUPDATE\s+|\bDELETE\s+|foreign key|duplicate key|constraint failed|violates .* constraint/i;
  if (technicalPattern.test(message)) return fallback;
  return message;
}

function readHeaderValue(headers: unknown, name: string): string | undefined {
  if (!headers) return undefined;
  if (typeof (headers as { get?: unknown }).get === "function") {
    const value = (headers as { get: (key: string) => unknown }).get(name);
    if (typeof value === "string" && value.trim()) return value;
  }
  if (isRecord(headers)) {
    const direct = headers[name] ?? headers[name.toLowerCase()] ?? headers[name.toUpperCase()];
    if (typeof direct === "string" && direct.trim()) return direct;
    if (Array.isArray(direct) && typeof direct[0] === "string") return direct[0];
  }
  return undefined;
}

function readRecordString(value: unknown, key: string): string | undefined {
  return isRecord(value) ? readString(value[key]) : undefined;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function readUnknownArray(value: unknown, key: string): unknown[] {
  if (!isRecord(value)) return [];
  const candidate = value[key];
  return Array.isArray(candidate) ? candidate : [];
}

function firstString(...values: readonly (string | undefined)[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value;
  }
  return "unexpected_error";
}

function isAbortLike(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  if (error instanceof Error && /aborted|cancelled|canceled/i.test(error.message)) return true;
  if (isRecord(error) && readString(error.code) === "ERR_CANCELED") return true;
  return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function unique(values: readonly string[]): string[] {
  return Array.from(new Set(values.filter((item) => item.trim().length > 0)));
}
